import { Link } from "react-router-dom"
import '../AnnouncmentManagement/Annoucement.css'
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import AuthService from "../../../api/services/AuthService";
import { alertErrorMessage } from "../../../customComponents/CustomAlertMessage";
import { useEffect, useState, useCallback, useMemo } from "react";
import { ApiConfig } from "../../../api/apiConfig/apiConfig";
import { Helmet } from "react-helmet-async";

const getTokenIcon = (item) => {
    if (item?.logo) {
        const logo = item.logo;
        return logo.startsWith("http") ? logo : `${ApiConfig?.baseImage}${logo}`;
    }
    const base = item?.base_currency_id;
    const icon = base?.icon_path;
    if (!icon) return "/images/tether_icon.png";
    return icon?.startsWith("http") ? icon : `${ApiConfig?.baseImage}${icon}`;
};
const getTokenName = (item) =>
    item?.tokenName || item?.base_currency_id?.name || item?.base_currency_id?.short_name || "Token";
const getTokenSymbol = (item) =>
    item?.tokenSymbol || item?.base_currency_id?.short_name || "N/A";
const getQuoteSymbol = (item) => item?.acceptedCurrency || item?.quote_currency_id?.short_name || "USDT";
const getTokensForSale = (item) => item?.availableTokens ?? item?.tokensForSale ?? 0;
const getParticipants = (item) => item?.totalParticipants ?? item?.participantsCount ?? 0;

/** Tab key → matches API `status` (uppercase) */
const itemMatchesTab = (item, tab) => {
    const s = String(item?.status || "").toUpperCase();
    if (tab === "live") return s === "LIVE";
    if (tab === "upcoming") return s === "UPCOMING";
    if (tab === "ended") return s === "ENDED" || s === "CANCELLED";
    return false;
};

const formatLaunchpadTime = (d) => (d ? new Date(d).toLocaleString() : "-");

/** Remaining time until `targetMs` (days + HH:MM:SS) */
const formatHmsCountdown = (targetMs, pastLabel) => {
    if (!targetMs || Number.isNaN(targetMs)) return "—";
    let ms = targetMs - Date.now();
    if (ms <= 0) return pastLabel;
    const days = Math.floor(ms / 86400000);
    ms -= days * 86400000;
    const hours = Math.floor(ms / 3600000);
    ms -= hours * 3600000;
    const minutes = Math.floor(ms / 60000);
    ms -= minutes * 60000;
    const seconds = Math.floor(ms / 1000);
    const pad = (n) => String(n).padStart(2, "0");
    if (days > 0) return `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

/** LIVE: countdown to sale `endTime` */
const formatLiveEndsIn = (item) => {
    if (String(item?.status || "").toUpperCase() !== "LIVE") return null;
    const t = item?.endTime ? new Date(item.endTime).getTime() : NaN;
    return formatHmsCountdown(t, "Sale ended");
};

/** Fixed DD/MM/YYYY (e.g. UPCOMING start / LIVE end) */
const formatFixedDateOnly = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return "—";
    return dt.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
};

/** Fixed local time (countdown target ke saath wahi moment) */
const formatFixedClockOnly = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return "—";
    return dt.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
};

/** UPCOMING: live countdown until `startTime` */
const formatUpcomingStartsIn = (item) => {
    if (String(item?.status || "").toUpperCase() !== "UPCOMING") return null;
    const t = item?.startTime ? new Date(item.startTime).getTime() : NaN;
    return formatHmsCountdown(t, "Started");
};

/** UPCOMING handled in JSX; LIVE handled in JSX; ENDED/CANCELLED → start + end */
const getScheduleTimeRows = (item) => {
    const s = String(item?.status || "").toUpperCase();
    const start = item?.startTime;
    const end = item?.endTime;
    if (s === "UPCOMING") {
        return [];
    }
    if (s === "LIVE") {
        return [];
    }
    if (s === "ENDED" || s === "CANCELLED") {
        return [
            { label: "Start Time", value: formatLaunchpadTime(start) },
            { label: "End Time", value: formatLaunchpadTime(end) },
        ];
    }
    return [{ label: "End Time", value: formatLaunchpadTime(end) }];
};

const LaunchpadHome = () => {
    const [allLaunchpadList, setAllLaunchPadList] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
    const [filter, setFilter] = useState("live");
    /** Re-render every 1s for LIVE / UPCOMING countdowns */
    const [, setNowTick] = useState(0);
    const [heroStats, setHeroStats] = useState({ totalRaised: 0, totalParticipants: 0, listedProjects: 0 });

    const fetchLaunchpads = useCallback(async () => {
        LoaderHelper.loaderStatus(true);
        try {
            const result = await AuthService.getLaunchpads();
            LoaderHelper.loaderStatus(false);
            if (result?.success) {
                const raw = result?.data;
                const data = Array.isArray(raw) ? raw : [];
                setAllLaunchPadList(data);
                if (result?.pagination) {
                    setPagination(result.pagination);
                } else {
                    setPagination({ page: 1, limit: data.length, total: data.length, totalPages: 1 });
                }
                const totalRaised = data.reduce((a, i) => a + (parseFloat(i.totalRaised) || 0), 0);
                const totalParticipants = data.reduce((a, i) => a + (parseFloat(i.totalParticipants) || 0), 0);
                setHeroStats({
                    totalRaised,
                    totalParticipants,
                    listedProjects: data.length,
                });
            } else {
                setAllLaunchPadList([]);
                // If the API simply has no launchpads yet, don't show an error alert —
                // the empty-state UI already handles this gracefully.
                const msg = (result?.message || "").toLowerCase();
                const isEmptyState =
                    msg.includes("no launchpad") ||
                    msg.includes("not found") ||
                    msg.includes("no data") ||
                    msg.includes("empty");
                if (!isEmptyState) {
                    alertErrorMessage(result?.message || "Something went wrong while fetching launchpads.");
                }
            }
        } catch {
            LoaderHelper.loaderStatus(false);
            alertErrorMessage("Error loading launchpad data.");
            setAllLaunchPadList([]);
        }
    }, []);

    useEffect(() => {
        fetchLaunchpads();
    }, [fetchLaunchpads]);

    const filteredProjects = useMemo(
        () => allLaunchpadList.filter((item) => itemMatchesTab(item, filter)),
        [allLaunchpadList, filter],
    );

    useEffect(() => {
        const hasCountdown = allLaunchpadList.some((item) => {
            const s = String(item?.status || "").toUpperCase();
            return s === "LIVE" || s === "UPCOMING";
        });
        if (!hasCountdown) return undefined;
        const interval = setInterval(() => setNowTick((n) => n + 1), 1000);
        return () => clearInterval(interval);
    }, [allLaunchpadList]);

    const handleFilterChange = (tab) => {
        setFilter(tab);
    };

    return (
        <>
            <Helmet>
                <title>Token Launch Platform | Launchpad</title>
                <meta name="description" content="Join Launchpad – early-stage projects, exclusive token sales. Be first to new tokens." />
                <meta name="keywords" content="token sale platform, Web3 launchpad, early crypto investment, tokens" />
            </Helmet>

            <div className="launchpad_hero_s">
                <div className="container">
                    <div className="row">
                        <div className="col-sm-7">
                            <div className="cnt_banner">
                                <h1>Launchpad</h1>
                                <p>Your Easiest Way to Top Tokens — Early or at a Discount</p>

                                <ul className="launchpadlist">
                                    <li><span>Total Raised (USDT)</span>{heroStats.totalRaised?.toLocaleString(undefined, { maximumFractionDigits: 2 }) || "0"}</li>
                                    <li><span>Total Participants</span>{heroStats.totalParticipants?.toLocaleString() || "0"}</li>
                                    <li><span>Listed Projects</span>{heroStats.listedProjects || pagination.total || "0"}</li>
                                </ul>
                            </div>
                        </div>
                        <div className="col-sm-5">
                            <div className="hero_img">
                                <img src="/images/AnnouncementImg/launchpad_hero_img.png" alt="Launchpad" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <section className="project_coin">
                <div className="container">
                    <h2>Projects</h2>

                    <div className="filter_tabs">
                        <button className={`btn ${filter === "live" ? "active" : ""}`} onClick={() => handleFilterChange("live")}>Live</button>
                        <button className={`btn ${filter === "upcoming" ? "active" : ""}`} onClick={() => handleFilterChange("upcoming")}>Upcoming</button>
                        <button className={`btn ${filter === "ended" ? "active" : ""}`} onClick={() => handleFilterChange("ended")}>Ended</button>
                    </div>

                    <div className="table-responsive_tow">
                        <div className="coin_data_table">
                            {filteredProjects?.length > 0 ? (
                                filteredProjects.map((item) => (
                                    <div className="project_crypto_bl" key={item._id}>
                                        <div className="project_top">
                                            <div className="coin_lft">
                                                <div className="coin">
                                                    <img src={getTokenIcon(item)} alt={getTokenSymbol(item)} />
                                                </div>
                                                <div className="coin_cnt">
                                                    <div className="hd d-flex">
                                                        <h3>{getTokenSymbol(item)}</h3>
                                                        <ul className="subcate">
                                                            <li className={["ended", "cancelled"].includes((item?.status || "").toLowerCase()) ? "darkbg" : (item?.status || "").toLowerCase() === "live" ? "greendark" : (item?.status || "").toLowerCase() === "upcoming" ? "yellodark" : "darkbg"}>
                                                                <button>{(item?.status || "N/A")}</button>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                    <span>{getTokenName(item)}</span>
                                                </div>
                                            </div>
                                            <div className="total_time_ri">
                                                <ul>
                                                    <li><span>Total Distribution</span> {getTokensForSale(item).toLocaleString()} {getTokenSymbol(item)}</li>
                                                    {String(item?.status || "").toUpperCase() === "LIVE" ? (
                                                        <>
                                                            <li key={`${item._id}-end-at`}>
                                                                <span>End Time</span>{" "}
                                                                {formatFixedDateOnly(item?.endTime)}
                                                                {", "}
                                                                <span className="launchpad_fixed_clock">{formatFixedClockOnly(item?.endTime)}</span>
                                                            </li>
                                                            <li key={`${item._id}-ends-in`}>
                                                                <span>Ends in</span>{" "}
                                                                <span className="launchpad_countdown_live">{formatLiveEndsIn(item)}</span>
                                                            </li>
                                                        </>
                                                    ) : String(item?.status || "").toUpperCase() === "UPCOMING" ? (
                                                        <>
                                                            <li key={`${item._id}-start-date`}>
                                                                <span>Start Time</span>{" "}
                                                                {formatFixedDateOnly(item?.startTime)}
                                                                {", "}
                                                                <span className="launchpad_fixed_clock">{formatFixedClockOnly(item?.startTime)}</span>
                                                            </li>
                                                            <li key={`${item._id}-starts-in`}>
                                                                <span>Starts in</span>{" "}
                                                                <span className="launchpad_countdown_live">{formatUpcomingStartsIn(item)}</span>
                                                            </li>
                                                        </>
                                                    ) : (
                                                        getScheduleTimeRows(item).map((row) => (
                                                            <li key={`${item._id}-${row.label}`}><span>{row.label}</span> {row.value}</li>
                                                        ))
                                                    )}
                                                </ul>
                                            </div>
                                        </div>

                                        <div className="d-flex subscribe_data_info">
                                            <div className="subscribe_user_bl">
                                                <div className="d-flex userinfo_top">
                                                    <div className="subscribe_user_lft">
                                                        <img src={item?.quote_currency_id?.icon_path ? (item.quote_currency_id.icon_path.startsWith("http") ? item.quote_currency_id.icon_path : `${ApiConfig?.baseImage}${item.quote_currency_id.icon_path}`) : "/images/tether_icon.png"} alt={getQuoteSymbol(item)} />
                                                        <div className="subscribe_cnt">
                                                            <h4>{getQuoteSymbol(item)}</h4>
                                                            <p>Commit {getQuoteSymbol(item)} to Subscribe {getTokenSymbol(item)}</p>
                                                        </div>
                                                    </div>
                                                    <div className="user_right">
                                                        <i className="ri-user-line"></i>{getParticipants(item)}
                                                    </div>
                                                </div>

                                                <ul className="exclusivelist">
                                                    <li>Token Price <span className="text-yellow">1 {getTokenSymbol(item)} = {(item?.tokenPrice || 0).toLocaleString()} {getQuoteSymbol(item)}</span></li>
                                                    <li>Total Allocation <span>{getTokensForSale(item).toLocaleString()} {getTokenSymbol(item)}</span></li>
                                                    <li>Total Committed <span>{(item?.totalRaised || item?.totalInvested || 0).toLocaleString()} {getQuoteSymbol(item)}</span></li>
                                                </ul>
                                                <button className="viewdel_btn">
                                                    <Link to={`/launchpadCoin_Details/${item._id}`}>View Details</Link>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <>
                                    <div className="no_data_vector">
                                    <img src="/images/no_data_vector.svg" className="img-fluid dark_img" width="96" height="96" alt="" /><img src="/images/no_data_vector_light.png" className="img-fluid light_img" alt=""/>
                                    </div>
                                    <p className="text-center">No {filter.toLowerCase()} projects found.</p>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="viewmorebtn">
                        {/* <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                if (pagination.totalPages > 1 && pagination.page < pagination.totalPages) {
                                    fetchLaunchpads(filter, pagination.page + 1, true);
                                }
                            }}
                        >
                            View More
                        </a> */}
                    </div>
                </div>
            </section>
        </>
    );
};

export default LaunchpadHome;
